import {
  Globe, CheckCircle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type { Language } from '@/features/settings';

interface OrganizationViewProps {
  readonly languages: Language[];
  readonly isLoading: boolean;
  readonly langStats: {
    total: number;
    enabled: number;
    avgProgress: number 
  };
}

export function OrganizationView({
  languages, isLoading, langStats 
}: OrganizationViewProps) {
  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Languages</CardTitle>
            <Globe className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{langStats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Enabled</CardTitle>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{langStats.enabled}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Translation</CardTitle>
            <Globe className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{langStats.avgProgress}%</div>
          </CardContent>
        </Card>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`lang-skeleton-${i.toString()}`} className="h-16 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>Language Configuration</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {languages.map((lang) => (
                <LanguageRow key={lang.code} language={lang} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function getProgressColor(progress: number): string {
  if (progress === 100) return 'bg-green-500';
  if (progress >= 80) return 'bg-blue-500';
  return 'bg-amber-500';
}

function LanguageRow({ language }: { readonly language: Language }) {
  const progressColor = getProgressColor(language.translationProgress);

  const statusColor = language.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';

  return (
    <div className="flex items-center justify-between p-4 hover:bg-muted/50">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-bold uppercase">
          {language.code}
        </div>
        <div>
          <p className="font-medium">
            {language.name} <span className="text-muted-foreground">({language.nativeName})</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Last updated: {new Date(language.lastUpdated).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-32">
          <div className="flex justify-between text-xs mb-1">
            <span>Translation</span>
            <span>{language.translationProgress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted">
            <div className={`h-2 rounded-full ${progressColor}`} style={{ width: `${language.translationProgress}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {language.isDefault && (
            <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">Default</span>
          )}
          <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
            {language.isEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>
    </div>
  );
}
